import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Xarxa de seguretat: una pantalla que peta no ha de deixar el blanc.
 *
 * El 06/08/2026 van sortir dos casos de pàgina en blanc sense cap missatge —un
 * objecte multilingüe escrit al JSX, i abans un altre—: React llença, desmunta
 * l'arbre sencer i no queda res a la pantalla. En una aula, amb un examen
 * corrent, la diferència entre «s'ha trencat, torna-ho a carregar» i el blanc
 * és tot.
 *
 * Només pot ser una classe: `componentDidCatch` no té equivalent amb hooks. El
 * missatge sí que és una funció, i per això pot fer servir les traduccions.
 *
 * A `App.js` va **dins** de `<main>` i per sota de la navegació, de manera que
 * si una pantalla peta la barra de dalt segueix servint per marxar-ne. I porta
 * `key={location.pathname}`: en canviar de pàgina es reinicia sol.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Queda a la consola del navegador, que és on ho mirarà qui ho depuri.
    console.error('La pantalla s\'ha aturat:', error, info?.componentStack);
  }

  render() {
    if (this.state.error) {
      return <PantallaAturada error={this.state.error} />;
    }
    return this.props.children;
  }
}

function PantallaAturada({ error }) {
  const { t } = useTranslation();

  return (
    <div className="pantalla-aturada">
      <h1>{t('errors.screenStoppedTitle')}</h1>
      <p>{t('errors.screenStoppedBody')}</p>

      <div className="pantalla-aturada-accions">
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          {t('errors.reloadPage')}
        </button>
        <Link to="/" className="btn btn-secondary">
          {t('errors.goHome')}
        </Link>
      </div>

      <details className="pantalla-aturada-detall">
        <summary>{t('errors.technicalDetail')}</summary>
        <pre>{String(error?.message || error)}</pre>
      </details>
    </div>
  );
}

export default ErrorBoundary;
